import { Box, Button, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { capitalize } from 'remeda';
import type { onSubmitFunction } from 'types/react-hook-form';
import { VarType } from '../CustomVariables.types';

type CustomVariablesFormValues = {
  custom_variables: Record<string, string | number | Date | boolean | null>;
};

type CustomVariablesFormProps = {
  onSubmit: onSubmitFunction;
  initialValues: CustomVariablesFormValues;
};

export default function CustomVariablesForm({ onSubmit, initialValues }: CustomVariablesFormProps) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const normalizedInitialValues: CustomVariablesFormValues = {
    custom_variables: Object.fromEntries(
      Object.entries(initialValues.custom_variables).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? String(value) : value,
      ]),
    ),
  };

  const form = useForm<CustomVariablesFormValues>({
    initialValues: normalizedInitialValues,
  });

  const isDirty = form.isDirty();

  const submit = (values: CustomVariablesFormValues) => {
    onSubmit({ ...values });
  };

  const customVariables = form.values.custom_variables;

  const accurateInput = (customVarKey: string, varType: VarType | undefined) => {
    const label = capitalize(customVarKey.replaceAll('_', ' '));

    switch (varType) {
      case VarType.BOOLEAN:
        return (
          <Select
            data={[
              { label: t(i18nKeys.YES), value: 'true' },
              { label: t(i18nKeys.NO), value: 'false' },
            ]}
            label={label}
            key={form.key(`custom_variables.${customVarKey}`)}
            {...form.getInputProps(`custom_variables.${customVarKey}`)}
          />
        );
      case VarType.DATE:
        return (
          <DatePickerInput
            placeholder=" "
            label={label}
            key={form.key(`custom_variables.${customVarKey}`)}
            {...form.getInputProps(`custom_variables.${customVarKey}`)}
          />
        );
      case VarType.NUMBER:
        return (
          <NumberInput
            label={label}
            decimalScale={2}
            key={form.key(`custom_variables.${customVarKey}`)}
            {...form.getInputProps(`custom_variables.${customVarKey}`)}
          />
        );
      default:
        return (
          <TextInput
            label={label}
            key={form.key(`custom_variables.${customVarKey}`)}
            {...form.getInputProps(`custom_variables.${customVarKey}`)}
          />
        );
    }
  };

  const renderInput = (customVarKey: string) => {
    const varType = company.custom_variables.find(el => el.column_name === customVarKey)
      ?.var_type as VarType | undefined;

    return <Box key={customVarKey}>{accurateInput(customVarKey, varType)}</Box>;
  };

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack>
        {Object.keys(customVariables)
          .sort((a, b) => a.localeCompare(b))
          .map(customVarKey => renderInput(customVarKey))}
        <Button disabled={!isDirty} type="submit">
          {t(i18nKeys.SAVE)}
        </Button>
      </Stack>
    </form>
  );
}
